import {
  queryOptions,
  useMutation,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
} from "@tanstack/react-query"

import { ApiError } from "@/lib/errors"
import { http } from "@/lib/http"
import { qk } from "@/lib/query-keys"
import { PRIMARY_CLIENT_CONTACT_ROLES, STAFF_ROLES } from "@/lib/roles"

import {
  isPrimaryClientContactNoLongerActiveError,
  NewEngagementFieldError,
  PRIMARY_CLIENT_CONTACT_NO_LONGER_ACTIVE,
  type ClientId,
  type CreateClientInput,
  type PrimaryClientContact,
  type StaffOption,
  type UserId,
} from "../clients-data"

import type { ClientOutWire } from "./clients"
import {
  toActiveUserOptionsByName,
  toUsersById,
  toWireUserIdOrNull,
  usersWithRolesQuery,
  type UserOutWire,
} from "./users"

interface ClientPrimaryClientContactPatchWire {
  primary_client_contact_id: number | null
}

function hasPrimaryClientContactRole(staffMember: UserOutWire): boolean {
  return (PRIMARY_CLIENT_CONTACT_ROLES as readonly string[]).includes(staffMember.role)
}

function toPrimaryClientContactStatus(staffMember: UserOutWire): PrimaryClientContact["status"] {
  if (!staffMember.is_active) return "deactivated"
  if (!hasPrimaryClientContactRole(staffMember)) return "viewOnly"
  return "active"
}

export function toPrimaryClientContact(
  primaryClientContactId: UserId | null,
  staffById: ReadonlyMap<number, UserOutWire> | undefined,
): PrimaryClientContact | null {
  if (primaryClientContactId === null) return null
  const nameWithoutStaffRecord = `User #${primaryClientContactId}`
  if (staffById === undefined) {
    return { userId: primaryClientContactId, name: nameWithoutStaffRecord, status: "unresolved" }
  }
  const staffMember = staffById.get(Number(primaryClientContactId))
  if (!staffMember) {
    return { userId: primaryClientContactId, name: nameWithoutStaffRecord, status: "notStaff" }
  }
  return {
    userId: primaryClientContactId,
    name: staffMember.full_name ?? staffMember.email,
    status: toPrimaryClientContactStatus(staffMember),
  }
}

function toPrimaryClientContactCandidates(staff: UserOutWire[]): StaffOption[] {
  return toActiveUserOptionsByName(staff.filter(hasPrimaryClientContactRole))
}

export const staffRosterQuery = () => usersWithRolesQuery(STAFF_ROLES)

export const primaryClientContactCandidatesQuery = () =>
  queryOptions({
    ...staffRosterQuery(),
    select: toPrimaryClientContactCandidates,
  })

export const staffByIdQuery = () =>
  queryOptions({
    ...staffRosterQuery(),
    select: toUsersById,
  })

export async function setPrimaryClientContact(
  clientId: ClientId,
  primaryClientContactId: UserId | null,
): Promise<void> {
  const path = `/api/v1/clients/${clientId}`

  const body: ClientPrimaryClientContactPatchWire = {
    primary_client_contact_id: toWireUserIdOrNull(primaryClientContactId),
  }

  await http.patch<ClientOutWire, ClientPrimaryClientContactPatchWire>(path, body)
}

async function isStillPrimaryClientContactCandidate(
  queryClient: QueryClient,
  primaryClientContactId: UserId,
): Promise<boolean> {
  const freshStaff = await queryClient.fetchQuery({
    ...staffRosterQuery(),
    staleTime: 0,
    retry: false,
  })
  return freshStaff.some(
    (staffMember) =>
      String(staffMember.id) === primaryClientContactId &&
      staffMember.is_active &&
      hasPrimaryClientContactRole(staffMember),
  )
}

const assumeStillPrimaryClientContactCandidate = () => true

export async function resolveNewEngagementFieldError(
  queryClient: QueryClient,
  createError: unknown,
  draft: CreateClientInput,
): Promise<NewEngagementFieldError | null> {
  const sentPrimaryClientContactId = draft.primaryClientContactId

  if (isPrimaryClientContactNoLongerActiveError(createError, sentPrimaryClientContactId)) {
    void queryClient.invalidateQueries({ queryKey: staffRosterQuery().queryKey })
    return new NewEngagementFieldError(
      "primaryClientContact",
      PRIMARY_CLIENT_CONTACT_NO_LONGER_ACTIVE,
    )
  }

  if (!(createError instanceof ApiError) || createError.status !== 400) return null

  if (sentPrimaryClientContactId !== null) {
    const primaryClientContactStillCandidate = await isStillPrimaryClientContactCandidate(
      queryClient,
      sentPrimaryClientContactId,
    ).catch(assumeStillPrimaryClientContactCandidate)
    if (!primaryClientContactStillCandidate) {
      return new NewEngagementFieldError("primaryClientContact", createError.message)
    }
  }

  return new NewEngagementFieldError("name", createError.message)
}

export interface PrimaryClientContactChange {
  clientId: ClientId
  primaryClientContactId: UserId | null
}

export function useSetPrimaryClientContactMutation(): UseMutationResult<
  void,
  Error,
  PrimaryClientContactChange
> {
  const queryClient = useQueryClient()

  const sendPrimaryClientContactChange = (change: PrimaryClientContactChange) => {
    return setPrimaryClientContact(change.clientId, change.primaryClientContactId)
  }

  const invalidateEngagementLists = () => {
    void queryClient.invalidateQueries({ queryKey: qk.clients.all })
  }

  return useMutation({
    mutationFn: sendPrimaryClientContactChange,
    onSuccess: invalidateEngagementLists,
  })
}
